/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Typography, useTheme, Menu, MenuItem, ListItemText } from '@mui/material';
import { useSociete, useTiersService, Utilisateur } from '@europrocurement/l2d-domain';
import { ModalCreateNote } from '@europrocurement/flexy-components/components/organisms/ModalCreateNote';
import { ModalEditNote } from '@europrocurement/flexy-components/components/organisms/ModalEditNote';
import { ConfirmationModal } from '@europrocurement/flexy-components/components/organisms/ConfirmationModal';
import { useSnackbar } from 'notistack';
import { ActivityProps } from '@europrocurement/flexy-components/types/ActivityHistoryTypes';
import { useModal } from '@europrocurement/flexy-components/providers';
import { ContactTiersReadNoteGroupTimestampableBlameambleGroup } from '@europrocurement/l2d-domain/openApi/ApiTiers';
import { ActivityItem } from './ActivityItem';
import { EmptyState } from './EmptyState';
import { ActivitiesHeader } from './ActivitiesHeader';

const NOTE_DELETE_MESSAGES = {
    question: 'Supprimer cette note ?',
    detail: {
        message: 'Cette action est irréversible. La note sera définitivement supprimée.',
        severity: 'warning' as const,
    },
    validation: 'Supprimer',
    annulation: 'Annuler',
};

type ActivityFilter = 'all' | 'notes' | 'important_notes' | 'actions';

const FILTER_LABELS: Record<ActivityFilter, string> = {
    all: 'Toutes les activités',
    notes: 'Notes uniquement',
    important_notes: 'Notes importantes',
    actions: 'Actions uniquement',
};

export type ContactActivitiesPreviewProps = {
    contact: ContactTiersReadNoteGroupTimestampableBlameambleGroup;
    resolveUserInfo: (email: string) => Promise<Utilisateur | null>;
};

const formatUserDisplayName = (
    firstName?: string,
    lastName?: string,
): { firstName: string; lastName: string } => {
    if (!firstName || !lastName) {
        return { firstName: '', lastName: '' };
    }

    return {
        firstName: firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase(),
        lastName: lastName.toUpperCase(),
    };
};

export const ContactActivitiesPreview: React.FunctionComponent<ContactActivitiesPreviewProps> =
    function ({ contact, resolveUserInfo }) {
        const [activities, setActivities] = useState<ActivityProps[]>([]);
        const [filteredActivities, setFilteredActivities] = useState<ActivityProps[]>([]);
        const [isLoading, setIsLoading] = useState(false);
        const [isEditingNote, setIsEditingNote] = useState(false);
        const [isDeletingNote, setIsDeletingNote] = useState(false);
        const [currentFilter, setCurrentFilter] = useState<ActivityFilter>('all');
        const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
        const [isScrollFocused, setIsScrollFocused] = useState(false);

        const containerRef = useRef<HTMLDivElement>(null);
        const scrollContainerRef = useRef<HTMLDivElement>(null);

        const theme = useTheme();
        const { modalActions } = useModal();
        const { enqueueSnackbar } = useSnackbar();
        const { contactsApi } = useTiersService();
        const xIdSociete = useSociete();

        const convertNoteToActivity = useCallback(
            async (note: any): Promise<ActivityProps> => {
                const createdByUser = note.createdBy ? await resolveUserInfo(note.createdBy) : null;
                const updatedByUser = note.updatedBy ? await resolveUserInfo(note.updatedBy) : null;

                const createdByFormatted = createdByUser
                    ? formatUserDisplayName(createdByUser.prenom, createdByUser.nom)
                    : { firstName: note.createdBy || 'Anonyme', lastName: '' };

                const updatedByFormatted = updatedByUser
                    ? formatUserDisplayName(updatedByUser.prenom, updatedByUser.nom)
                    : { firstName: note.updatedBy || '', lastName: '' };

                const userId = createdByUser?.user || note.createdBy || 'unknown';

                return {
                    id: `note-${note.id}`,
                    type: 'note',
                    noteType: note.highlighted ? 'important' : 'standard',
                    content: note.content || '',
                    createdAt: note.createdAt ? new Date(note.createdAt) : new Date(),
                    userId: typeof userId === 'string' ? userId : String(userId),
                    userFirstName: createdByFormatted.firstName,
                    userLastName: createdByFormatted.lastName,
                    updatedAt: note.updatedAt ? new Date(note.updatedAt) : undefined,
                    updatedBy: note.updatedBy || undefined,
                    updatedByFirstName: updatedByFormatted.firstName,
                    updatedByLastName: updatedByFormatted.lastName,
                };
            },
            [resolveUserInfo],
        );

        const applyFilter = useCallback(
            (activitiesList: ActivityProps[], filter: ActivityFilter) => {
                if (filter === 'notes') {
                    return activitiesList.filter((activity) => activity.type === 'note');
                }
                if (filter === 'important_notes') {
                    return activitiesList.filter(
                        (activity) => activity.type === 'note' && activity.noteType === 'important',
                    );
                }
                if (filter === 'actions') {
                    return activitiesList.filter((activity) => activity.type === 'action');
                }
                return activitiesList;
            },
            [],
        );

        const loadActivities = useCallback(async () => {
            if (!contact.id) return;

            setIsLoading(true);
            try {
                const response = await contactsApi.getNotesContactContactsItem({
                    id: contact.id,
                    xIdSociete,
                });

                const notes = response.data.notes || [];
                const convertedActivities = await Promise.all(notes.map(convertNoteToActivity));

                const sortedActivities = convertedActivities.sort(
                    (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
                );

                setActivities(sortedActivities);
            } catch (error) {
                console.error('Erreur lors du chargement des activités:', error);
                enqueueSnackbar('Erreur lors du chargement des activités', { variant: 'error' });
            } finally {
                setIsLoading(false);
            }
        }, [contact.id, contactsApi, xIdSociete, convertNoteToActivity, enqueueSnackbar]);

        useEffect(() => {
            loadActivities();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        useEffect(() => {
            setFilteredActivities(applyFilter(activities, currentFilter));
        }, [activities, currentFilter, applyFilter]);

        // Gestion du focus pour le scroll
        useEffect(() => {
            const handleClickOutside = (event: MouseEvent) => {
                if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                    setIsScrollFocused(false);
                }
            };

            const handleGlobalWheel = (event: WheelEvent) => {
                if (isScrollFocused && scrollContainerRef.current) {
                    const container = scrollContainerRef.current;
                    const { scrollTop, scrollHeight, clientHeight } = container;

                    // Calculer la nouvelle position de scroll
                    const newScrollTop = scrollTop + event.deltaY;

                    // Vérifier si on peut scroller dans cette direction
                    const canScrollDown = scrollTop < scrollHeight - clientHeight;
                    const canScrollUp = scrollTop > 0;

                    // Si on peut scroller dans la direction demandée, on intercepte
                    if ((event.deltaY > 0 && canScrollDown) || (event.deltaY < 0 && canScrollUp)) {
                        event.preventDefault();
                        event.stopPropagation();

                        // Appliquer le scroll avec les limites
                        container.scrollTop = Math.max(
                            0,
                            Math.min(newScrollTop, scrollHeight - clientHeight),
                        );
                    } else if (
                        (event.deltaY > 0 && !canScrollDown) ||
                        (event.deltaY < 0 && !canScrollUp)
                    ) {
                        // Si on atteint les limites, on intercepte quand même pour éviter le scroll de la page
                        event.preventDefault();
                        event.stopPropagation();
                    }
                }
            };

            if (isScrollFocused) {
                document.addEventListener('click', handleClickOutside);
                // Utiliser capture: true pour intercepter l'événement avant qu'il ne bulle
                document.addEventListener('wheel', handleGlobalWheel, {
                    passive: false,
                    capture: true,
                });
            }

            return () => {
                document.removeEventListener('click', handleClickOutside);
                document.removeEventListener('wheel', handleGlobalWheel, true);
            };
        }, [isScrollFocused]);

        const handleContainerClick = () => {
            setIsScrollFocused(true);
        };

        const handleCreateNote = () => {
            modalActions.call(
                <ModalCreateNote
                    onConfirm={async (content: string, highlighted: boolean) => {
                        if (!contact.id) return;

                        try {
                            await contactsApi.addNoteContactContactsItem({
                                id: contact.id.toString(),
                                addNoteContactContactsItemRequest: { content, highlighted },
                                xIdSociete,
                            });

                            await loadActivities();
                            modalActions.close();
                            enqueueSnackbar('Note créée avec succès');
                        } catch (error) {
                            console.error('Erreur lors de la création de la note:', error);
                            enqueueSnackbar('Erreur lors de la création de la note', {
                                variant: 'error',
                            });
                        }
                    }}
                />,
            );
        };

        const handleEditNote = (noteId: string, content: string, highlighted: boolean) => {
            modalActions.call(
                <ModalEditNote
                    initialContent={content}
                    initialHighlighted={highlighted}
                    isSaving={isEditingNote}
                    onConfirm={async (newContent: string, newHighlighted: boolean) => {
                        if (!contact.id) return;

                        try {
                            setIsEditingNote(true);

                            await contactsApi.updateNoteContactContactsItem({
                                id: contact.id,
                                noteId: +noteId.replace('note-', ''),
                                addNoteContactContactsItemRequest: {
                                    content: newContent,
                                    highlighted: newHighlighted,
                                },
                                xIdSociete,
                            });

                            await loadActivities();
                            modalActions.close();
                            enqueueSnackbar('Note mise à jour avec succès');
                        } catch (error) {
                            console.error('Erreur lors de la modification de la note:', error);
                            enqueueSnackbar('Erreur lors de la modification de la note', {
                                variant: 'error',
                            });
                        } finally {
                            setIsEditingNote(false);
                        }
                    }}
                    onCancel={() => modalActions.close()}
                />,
            );
        };

        const handleDeleteNote = (noteId: string) => {
            modalActions.call(
                <ConfirmationModal
                    messages={NOTE_DELETE_MESSAGES}
                    color={theme.palette.error}
                    disabled={isDeletingNote}
                    actionOnValidation={async () => {
                        if (!contact.id) return;

                        try {
                            setIsDeletingNote(true);

                            await contactsApi.deleteNoteContactContactsItem({
                                id: contact.id,
                                noteId: +noteId.replace('note-', ''),
                                xIdSociete,
                            });

                            await loadActivities();
                            modalActions.close();
                            enqueueSnackbar('Note supprimée avec succès');
                        } catch (error) {
                            console.error('Erreur lors de la suppression de la note:', error);
                            enqueueSnackbar('Erreur lors de la suppression de la note', {
                                variant: 'error',
                            });
                        } finally {
                            setIsDeletingNote(false);
                        }
                    }}
                    actionOnCancellation={() => modalActions.close()}
                />,
            );
        };

        const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
            setFilterAnchorEl(event.currentTarget);
        };

        const handleFilterClose = () => {
            setFilterAnchorEl(null);
        };

        const handleFilterSelect = (filter: ActivityFilter) => {
            setCurrentFilter(filter);
            handleFilterClose();
        };

        const hasActivities = filteredActivities.length > 0;

        if (isLoading) {
            return (
                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '8px',
                        padding: '8px',
                        minHeight: '55px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <Typography
                        variant="l2dbodyxs"
                        sx={{ color: 'text.secondary' }}
                    >
                        Chargement...
                    </Typography>
                </Box>
            );
        }

        // Rendu principal (avec ou sans activités)
        return (
            <>
                <Box
                    ref={containerRef}
                    onClick={handleContainerClick}
                    sx={{
                        border: '2px solid',
                        borderColor: isScrollFocused ? theme.palette.primary.main : 'divider',
                        borderRadius: '8px',
                        padding: '8px',
                        minHeight: '55px',
                        maxHeight: '200px',
                        cursor: isScrollFocused ? 'default' : 'pointer',
                        transition: 'border-color 0.2s ease-in-out',
                        position: 'relative',
                    }}
                >
                    {/* Indicateur visuel de focus */}
                    {isScrollFocused && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: -1,
                                right: -1,
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText,
                                fontSize: '8px',
                                padding: '2px 4px',
                                borderRadius: '0 6px 0 4px',
                                lineHeight: 1,
                                fontWeight: 'bold',
                            }}
                        >
                            Scroll Actif
                        </Box>
                    )}

                    {/* Header ou EmptyState selon le cas */}
                    {hasActivities ? (
                        <ActivitiesHeader
                            activities={activities}
                            currentFilter={currentFilter}
                            onCreateNote={handleCreateNote}
                            onFilterClick={handleFilterClick}
                        />
                    ) : (
                        <EmptyState
                            currentFilter={currentFilter}
                            onCreateNote={handleCreateNote}
                            onFilterClick={handleFilterClick}
                        />
                    )}

                    {/* Liste des activités avec scroll focalisé */}
                    {hasActivities && (
                        <Box
                            ref={scrollContainerRef}
                            data-scroll-container
                            sx={{
                                maxHeight: '140px',
                                overflowY: 'auto',
                                '&::-webkit-scrollbar': { width: '4px' },
                                '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: isScrollFocused
                                        ? theme.palette.primary.main
                                        : 'divider',
                                    borderRadius: '2px',
                                },
                            }}
                        >
                            {filteredActivities.map((activity, index) => (
                                <ActivityItem
                                    key={activity.id}
                                    activity={activity}
                                    index={index}
                                    onEdit={handleEditNote}
                                    onDelete={handleDeleteNote}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Menu de filtrage - TOUJOURS PRÉSENT */}
                <Menu
                    anchorEl={filterAnchorEl}
                    open={Boolean(filterAnchorEl)}
                    onClose={handleFilterClose}
                    PaperProps={{ sx: { minWidth: '180px' } }}
                >
                    {Object.entries(FILTER_LABELS).map(([filterKey, label]) => (
                        <MenuItem
                            key={filterKey}
                            selected={currentFilter === filterKey}
                            onClick={() => handleFilterSelect(filterKey as ActivityFilter)}
                        >
                            <ListItemText primary={label} />
                        </MenuItem>
                    ))}
                </Menu>
            </>
        );
    };
